import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Pencil, Loader2, AlertCircle, Plus, Trash2, Tags, Search } from "lucide-react";
import { Card, CardContent } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@pgs-core/ui/components/alert-dialog";
import { PaginationControls } from "@pgs-core/ui/components/pagination-controls";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { useListControls } from "@pgs-core/portal-lib/list-controls";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { PostCategoriesDialog } from "../components/post-categories-dialog";
import { createPost, deletePost, fetchPostsPage, type PostListItem } from "../lib/posts";
import { postSlugProblem, slugifyPostTitle } from "../lib/post-slug";
import type { ClientOutletContext } from "../lib/outlet-context";

// D1 CURRENT_TIMESTAMP is "YYYY-MM-DD HH:MM:SS" in UTC — normalize to a Date.
function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value: string | null): string {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : "—";
}

// Live-state badge, mirroring the Pages list: a published post edited since
// the last build only goes live on the next Publish.
function postStatus(post: PostListItem, buildDispatchedAt: string | null | undefined) {
  if (!post.published) return { label: "Draft", variant: "secondary" as const };
  const dispatched = toDate(buildDispatchedAt);
  const updated = toDate(post.updated_at);
  const pending = !dispatched || (updated !== null && updated > dispatched);
  return pending
    ? { label: "Queued", variant: "warning" as const }
    : { label: "Published", variant: "success" as const };
}

export function PostsPage() {
  const { api, status, refreshStatus } = useOutletContext<ClientOutletContext>();
  const navigate = useNavigate();
  const controls = useListControls();
  const query = useApiQuery(
    () => (api ? fetchPostsPage(api, controls.queryString) : null),
    [api, controls.queryString]
  );
  const posts = query.data?.posts ?? null;
  const total = query.data?.total ?? 0;
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PostListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setNewTitle("");
    setNewSlug("");
    setSlugTouched(false);
    setCreateError("");
    setCreateOpen(true);
  };

  const onTitleChange = (value: string) => {
    setNewTitle(value);
    if (!slugTouched) setNewSlug(slugifyPostTitle(value));
  };

  const createSlugError = newSlug ? postSlugProblem(newSlug) : "";

  const create = async () => {
    if (!api) return;
    if (!newTitle.trim()) {
      setCreateError("Enter a post title.");
      return;
    }
    if (createSlugError) {
      setCreateError(createSlugError);
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const result = await createPost(api, { title: newTitle.trim(), slug: newSlug.trim() });
      setCreateOpen(false);
      refreshStatus?.();
      navigate(`/blog/${result.id}`);
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deletePost(api, deleteTarget.id);
      query.reload();
      setDeleteTarget(null);
      refreshStatus?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Blog"
        description="Write posts, organise them into categories, then publish the site to put them live at /blog."
        actions={
          <>
            <Button onClick={() => setCategoriesOpen(true)} variant="outline" size="sm">
              <Tags className="h-4 w-4" />
              Categories
            </Button>
            <Button onClick={openCreate} size="sm">
              <Plus className="h-4 w-4" />
              New post
            </Button>
          </>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={controls.search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
          placeholder="Search posts…"
          className="pl-8"
          aria-label="Search posts"
        />
      </div>

      {!posts && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {posts && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Categories</th>
                  <th className="px-4 py-3 font-medium">Author</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Publish date</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {posts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      {controls.q
                        ? "No posts match your search."
                        : "No posts yet — write your first post to get started."}
                    </td>
                  </tr>
                )}
                {posts.map((post) => {
                  const state = postStatus(post, status?.build_dispatched_at);
                  return (
                    <tr key={post.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{post.title}</div>
                        <div className="text-xs text-muted-foreground">/blog/{post.slug}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {post.categories.length === 0 && <span className="text-muted-foreground">—</span>}
                          {post.categories.map((category) => (
                            <Badge key={category.id} variant="secondary">{category.name}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{post.author ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={state.variant}
                          title={state.label === "Queued" ? "Set to publish — goes live on next Publish" : undefined}
                        >
                          {state.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(post.published_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/blog/${post.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                              Edit
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            title="Delete post"
                            onClick={() => setDeleteTarget(post)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationControls
              page={controls.page}
              total={total}
              perPage={controls.perPage}
              onPageChange={controls.setPage}
              onPerPageChange={controls.setPerPage}
              itemsLabel="posts"
              className="border-t px-4 py-3"
            />
          </CardContent>
        </Card>
      )}

      {/* Create post */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New post</DialogTitle>
            <DialogDescription>
              New posts start as a draft. Write your content, then publish the site to make it live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-post-title">Title</Label>
              <Input
                id="new-post-title"
                value={newTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
                placeholder="e.g. Five tips for spring gardens"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-post-slug">URL slug</Label>
              <Input
                id="new-post-slug"
                value={newSlug}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setNewSlug(e.target.value); setSlugTouched(true); }}
                placeholder="five-tips-for-spring-gardens"
                aria-invalid={!!createSlugError}
              />
              {createSlugError ? (
                <p className="text-xs text-destructive">{createSlugError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Lives at /blog/{newSlug || "your-post"}/</p>
              )}
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={() => { void create(); }} disabled={creating || !newTitle.trim() || !!createSlugError}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage categories */}
      {api && <PostCategoriesDialog
        open={categoriesOpen}
        onOpenChange={setCategoriesOpen}
        api={api}
        onChanged={() => {
          // Category renames/deletes change the list rows' badges.
          query.reload();
          refreshStatus?.();
        }}
      />}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the post from your portal. It stays on your live site until you next
              publish. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => { e.preventDefault(); void remove(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
